import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Flex,
    Grid,
    SimpleGrid,
    Text,
} from "@chakra-ui/react";
import {
    useDispatch,
    useSelector,
} from "react-redux";
import { Wallet,  
    BadgeCheck,
    Clock3,
    TrendingUp,
    ClockArrowUp,
    Users,
    UserPlus
 } from "lucide-react";

import routes from "../../routes";

import Sidebar from "../../components/Sidebar";
import TopBar from "../../components/Topbar";

import PageHeader from "./components/PageHeader";
import StatCard from "./components/StatCard";
import MiniStatCard from "./components/MiniStatCard";
import ReferralCard from "../Profile/components/ReferralCard";
import ProgressCard from "./components/ProgressCard";
import FunnelCard from "./components/FunnelCard";
import Leaderboard from "./components/LeaderBoard";
import RecentReferralsTable from "./components/RecentReferralsTable";
import ReferralGrowthChart from "./components/ReferralGrowthChart";
import CommissionTrendChart from "./components/CommissionTrendChart";

import {
    fetchReferredDriversAction,
    fetchReferralMilestonesAction,
} from "../../state/actions/driverPartnerActions";
import RegisterDriverModal from "../RegisterDriver/RegisterDriverModal";

const Dashboard = () => {
    const dispatch =
        useDispatch();

    const navigate =
        useNavigate();
    const [
    isRegisterOpen,
    setRegisterOpen,
] = useState(false);
    const {
        driverPartner,
        referredDrivers = [],
        referralMilestones = [],
        loading,
        loaded,
    } = useSelector(
        (state) =>
            state.driverPartner
    );

    useEffect(() => {
        dispatch(
            fetchReferredDriversAction(
                1,
                10
            )
        );

        dispatch(
            fetchReferralMilestonesAction()
        );
    }, [
        dispatch,
    ]);

useEffect(() => {
    if (!loaded) return;

    if (!driverPartner) {
        navigate(
            routes.completeProfile,
            { replace: true }
        );
    }
}, [
    loaded,
    driverPartner,
    navigate,
]);

    const user =
        driverPartner?.user;

    const fullName =
        [
            user?.first_name,
            user?.middle_name,
            user?.last_name,
        ]
            .filter(
                (v) =>
                    v?.trim()
            )
            .join(" ");

    const displayName =
        fullName ||
        user?.username ||
        "Driver Partner";

    const joinedDate =
        driverPartner?.created_at
            ? new Date(
                  driverPartner.created_at
              ).toLocaleDateString(
                  "en-US",
                  {
                      month:
                          "short",
                      year:
                          "numeric",
                  }
              )
            : "-";

const approvedDrivers =
    referredDrivers.filter(
        driver =>
            driver.driver
                ?.status ===
            "Approved"
    );

const pendingDrivers =
    referredDrivers.filter(
        driver =>
            driver.driver &&
            driver.driver
                ?.status !==
            "Approved"
    );

    const totalEarned =
        referralMilestones
            .filter(
                (
                    milestone
                ) =>
                    milestone.commission_status ===
                    "Paid"
            )
            .reduce(
                (
                    total,
                    milestone
                ) =>
                    total +
                    Number(
                        milestone.commission_amount ||
                            0
                    ),
                0
            );

    const pendingCommission =
        referralMilestones
            .filter(
                (
                    milestone
                ) =>
                    milestone.commission_status ===
                    "Pending"
            )
            .reduce(
                (
                    total,
                    milestone
                ) =>
                    total +
                    Number(
                        milestone.commission_amount ||
                            0
                    ),
                0
            );

const firstDeliveryCount =
    referralMilestones.filter(
        (
            milestone
        ) =>
            milestone.milestone_type ===
                "First Delivery Completed" &&
            milestone.milestone_status ===
                "Achieved"
    ).length;

const tenDeliveryCount =
    referralMilestones.filter(
        (
            milestone
        ) =>
            milestone.milestone_type ===
                "Ten Deliveries Completed" &&
            milestone.milestone_status ===
                "Achieved"
    ).length;

const thirtyDeliveryCount =
    referralMilestones.filter(
        (
            milestone
        ) =>
            milestone.milestone_type ===
                "Thirty Deliveries Completed" &&
            milestone.milestone_status ===
                "Achieved"
    ).length;

        const monthlyTarget = 10000;

const currentMonth = new Date().getMonth();
const currentYear = new Date().getFullYear();

const monthlyEarnings =
    referralMilestones
        .filter(
            milestone => {
                if (
                    milestone.commission_status !==
                    "Paid"
                ) {
                    return false;
                }

                const date =
                    new Date(
                        milestone.updated_at ||
                        milestone.created_at
                    );

                return (
                    date.getMonth() ===
                        currentMonth &&
                    date.getFullYear() ===
                        currentYear
                );
            }
        )
        .reduce(
            (
                sum,
                milestone
            ) =>
                sum +
                Number(
                    milestone.commission_amount ||
                    0
                ),
            0
        );

const monthlyGoalPercentage =
    Math.min(
        100,
        Math.round(
            (
                monthlyEarnings /
                monthlyTarget
            ) * 100
        )
    );
    return (
        <Flex
            h="100vh"
            bg="#F8FAFC"
            overflow="hidden"
        >
            <Box
                w={{
                    base: "0",
                    md: "280px",
                }}
                flexShrink={0}
            >
                <Sidebar />
            </Box>

            <Flex
                flex="1"
                direction="column"
                overflow="hidden"
            >
                <TopBar />

                <Box
                    flex="1"
                    overflowY="auto"
                    p={8}
                >
                    <PageHeader
                        title={`Welcome back, ${displayName}`}
                        subtitle={`Partner ID: ${
                            driverPartner?.partner_referral_code ||
                            "-"
                        } • Member since ${joinedDate}`}
                        primaryLabel="Register Driver"
                        primaryIcon={UserPlus}
                        onPrimaryClick={() =>
                            setRegisterOpen(
                                true
                            )
                        }
                    />

                    <Grid
                        templateColumns={{
                            base: "1fr",
                            xl: "2fr 320px",
                        }}
                        gap={6}
                    >
                        <Box>
                            <SimpleGrid
                                columns={{
                                    base: 1,
                                    md: 3,
                                }}
                                gap={6}
                            >
                                <StatCard
                                    title="Total Lifetime Earnings"
                                    value={`P${totalEarned.toFixed(
                                        2
                                    )}`}
                                    subtitle={`${approvedDrivers.length} approved referrals`}
                                    icon={
                                        <Wallet
                                            size={
                                                20
                                            }
                                            color="#2453FF"
                                        />
                                    }
                                />

                                <StatCard
                                    title="Pending Commission"
                                    value={`P${pendingCommission.toFixed(
                                        2
                                    )}`}
                                    subtitle="Awaiting payout"
                                    icon={
                                        <ClockArrowUp
                                            size={
                                                20
                                            }
                                            color="#2453FF"
                                        />
                                    }
                                />

                                <StatCard
                                    title="Total Recruits"
                                    value={
                                        referredDrivers.length
                                    }
                                     icon={
                                        <Users
                                            size={
                                                20
                                            }
                                            color="#2453FF"
                                        />
                                    }
                                    subtitle="Drivers recruited"
                                />
                                <StatCard
                                    title="Approved Drivers"
                                    value={
                                        approvedDrivers.length
                                    }
                                    subtitle="Eligible for milestone rewards"
                                    icon={
                                        <BadgeCheck
                                            size={20}
                                            color="#2453FF"
                                        />
                                    }
                                />

                                <StatCard
                                    title="Drivers Awaiting Approval"
                                    value={
                                        pendingDrivers.length
                                    }
                                    subtitle="Still completing onboarding"
                                    icon={
                                        <Clock3
                                            size={20}
                                            color="#2453FF"
                                        />
                                    }
                                />

                                <StatCard
                                    title="Average Earnings Per Driver"
                                    value={`P${
                                        approvedDrivers.length
                                            ? (
                                                totalEarned /
                                                approvedDrivers.length
                                            ).toFixed(2)
                                            : "0.00"
                                    }`}
                                    subtitle="Revenue generated per approved driver"
                                    icon={
                                        <TrendingUp
                                            size={20}
                                            color="#2453FF"
                                        />
                                    }
                                />
                            </SimpleGrid>
                            <Text
                            fontSize="xs"
                            color="gray.400"
                            fontWeight="700"
                            letterSpacing="2px"
                            mt={6}
                            mb={3}
                        >
                            RECRUITMENT MILESTONES ACHIEVED
                        </Text>
                            <SimpleGrid
                                columns={{
                                    base: 2,
                                    lg: 6,
                                }}
                                gap={3}
                                mt={6}
                            >
                                <MiniStatCard
                                    title="Pending"
                                    value={
                                        pendingDrivers.length
                                    }
                                />

                                <MiniStatCard
                                    title="Approved"
                                    value={
                                        approvedDrivers.length
                                    }
                                />

                                <MiniStatCard
                                    title="1st Delivery"
                                    value={
                                        firstDeliveryCount
                                    }
                                />

                                <MiniStatCard
                                    title="10 Deliveries"
                                    value={
                                        tenDeliveryCount
                                    }
                                />

                                <MiniStatCard
                                    title="30 Deliveries"
                                    value={
                                        thirtyDeliveryCount
                                    }
                                />

                               <MiniStatCard
                                    title="Conversion(Pending over Approved)"
                                    value={`${Math.round(
                                        referredDrivers.length
                                            ? (approvedDrivers.length /
                                                referredDrivers.length) *
                                                100
                                            : 0
                                    )}%`}
                                />
                            </SimpleGrid>
                        </Box>

                      <ReferralCard
                        driverPartner={
                            driverPartner
                        }
                        totalRecruits={
                            referredDrivers.length
                        }
                        totalEarned={
                            totalEarned
                        }
                    />

                                        </Grid>

                                        <Grid
                        mt={8}
                        gap={6}
                        templateColumns={{
                            base: "1fr",
                            xl: "2fr 320px",
                        }}
                        alignItems="start"
                    >
                        <ReferralGrowthChart
                            drivers={referredDrivers}
                        />

                        <Flex
                            direction="column"
                            gap={6}
                        >
                            <ProgressCard
                                title="Monthly Goal"
                                percentage={
                                    monthlyGoalPercentage
                                }
                                current={`P${monthlyEarnings.toFixed(
                                    2
                                )}`}
                                target={`P${monthlyTarget.toLocaleString()}`}
                            />

                            <FunnelCard
                                drivers={
                                    referredDrivers
                                }
                                milestones={
                                    referralMilestones
                                }
                            />
                        </Flex>
                    </Grid>

                    <Grid
                        mt={8}
                        gap={6}
                        templateColumns={{
                            base: "1fr",
                            xl: "2fr 370px",
                        }}
                    >
                        <RecentReferralsTable
                            drivers={
                                referredDrivers
                            }
                            milestones={
                                referralMilestones
                            }
                        />

                        <CommissionTrendChart
                            milestones={
                                referralMilestones
                            }
                        />
                    </Grid>
                </Box>
            </Flex>
            <RegisterDriverModal
            open={isRegisterOpen}
            onClose={() =>
                setRegisterOpen(
                    false
                )
            }
        />
        </Flex>
    );
};

export default Dashboard;